import React, { useState, useMemo } from 'react';
import './usertools-css/BMIBodyfatCalculator.css';

// --- Standards & Conversion Constants ---
const KG_PER_LB = 0.45359237;
const CM_PER_INCH = 2.54;

// WHO BMI Categories with strict Green / Orange / Red classifications
const WHO_BMI_CATEGORIES = [
  { max: 18.5, label: 'Underweight', badgeClass: 'badge-orange', color: '#f26522' },
  { max: 24.9, label: 'Normal weight', badgeClass: 'badge-green', color: '#10b981' },
  { max: 29.9, label: 'Pre-obesity', badgeClass: 'badge-orange', color: '#f26522' },
  { max: 34.9, label: 'Obesity Class I', badgeClass: 'badge-red', color: '#ef4444' },
  { max: 39.9, label: 'Obesity Class II', badgeClass: 'badge-red', color: '#dc2626' },
  { max: Infinity, label: 'Obesity Class III', badgeClass: 'badge-red', color: '#991b1b' },
];

// ACE Body Fat Categories mapped to Green / Orange / Red
const ACE_BODY_FAT_CATEGORIES = {
  male: [
    { range: '2-5%', max: 5, label: 'Essential Fat', color: '#f26522', badgeClass: 'badge-orange' },
    { range: '6-13%', max: 13.9, label: 'Athletes', color: '#10b981', badgeClass: 'badge-green' },
    { range: '14-17%', max: 17.9, label: 'Fitness', color: '#10b981', badgeClass: 'badge-green' },
    { range: '18-24%', max: 24.9, label: 'Average', color: '#f26522', badgeClass: 'badge-orange' },
    { range: '≥ 25%', max: Infinity, label: 'Obese', color: '#ef4444', badgeClass: 'badge-red' },
  ],
  female: [
    { range: '10-13%', max: 13, label: 'Essential Fat', color: '#f26522', badgeClass: 'badge-orange' },
    { range: '14-20%', max: 20.9, label: 'Athletes', color: '#10b981', badgeClass: 'badge-green' },
    { range: '21-24%', max: 24.9, label: 'Fitness', color: '#10b981', badgeClass: 'badge-green' },
    { range: '25-31%', max: 31.9, label: 'Average', color: '#f26522', badgeClass: 'badge-orange' },
    { range: '≥ 32%', max: Infinity, label: 'Obese', color: '#ef4444', badgeClass: 'badge-red' },
  ],
};

function formatNum(num, digits = 1) {
  if (num === null || num === undefined || Number.isNaN(num)) return '--';
  return Number(num).toFixed(digits);
}

function getBmiCategory(bmi) {
  if (!bmi || Number.isNaN(bmi) || bmi <= 0) {
    return { label: '--', badgeClass: 'badge-orange', color: '#f26522' };
  }
  for (const cat of WHO_BMI_CATEGORIES) {
    if (bmi <= cat.max) return cat;
  }
  return WHO_BMI_CATEGORIES[WHO_BMI_CATEGORIES.length - 1];
}

function getBodyFatCategory(bf, sex) {
  if (!bf || Number.isNaN(bf) || bf <= 0 || !sex) {
    return { label: '--', badgeClass: 'badge-orange', color: '#f26522' };
  }
  const categories = ACE_BODY_FAT_CATEGORIES[sex] || ACE_BODY_FAT_CATEGORIES.male;
  for (const cat of categories) {
    if (bf <= cat.max) return cat;
  }
  return categories[categories.length - 1];
}

function BMIBodyfatCalculator() {
  // Input states starting from scratch (empty)
  const [unit, setUnit] = useState('metric'); // 'metric' or 'imperial'
  const [sex, setSex] = useState('');
  const [age, setAge] = useState('');

  // Height & Weight states
  const [heightCm, setHeightCm] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [weightLb, setWeightLb] = useState('');

  // Circumferences (for U.S. Navy Method, WHtR & WHR)
  const [waist, setWaist] = useState('');
  const [neck, setNeck] = useState('');
  const [hip, setHip] = useState('');

  // Athletic Strength Inputs
  const [doesNotLift, setDoesNotLift] = useState(false);
  const [bench1RM, setBench1RM] = useState('');
  const [squat1RM, setSquat1RM] = useState('');
  const [curl1RM, setCurl1RM] = useState('');

  // Primary Goal
  const [goal, setGoal] = useState('maintain');

  // --- Dynamic Unit Switching (Converts Data Without Loss) ---
  const handleUnitToggle = (nextUnit) => {
    if (nextUnit === unit) return;
    if (nextUnit === 'imperial') {
      const hCm = parseFloat(heightCm);
      if (hCm > 0) {
        const totalInches = hCm / CM_PER_INCH;
        setHeightFt(String(Math.floor(totalInches / 12)));
        setHeightIn(String(Math.round(totalInches % 12)));
      } else {
        setHeightFt('');
        setHeightIn('');
      }
      const wKg = parseFloat(weightKg);
      if (wKg > 0) {
        setWeightLb(String(Math.round(wKg / KG_PER_LB)));
      } else {
        setWeightLb('');
      }
      if (waist) setWaist(String((parseFloat(waist) / CM_PER_INCH).toFixed(1)));
      if (neck) setNeck(String((parseFloat(neck) / CM_PER_INCH).toFixed(1)));
      if (hip) setHip(String((parseFloat(hip) / CM_PER_INCH).toFixed(1)));
      if (bench1RM) setBench1RM(String(Math.round(parseFloat(bench1RM) / KG_PER_LB)));
      if (squat1RM) setSquat1RM(String(Math.round(parseFloat(squat1RM) / KG_PER_LB)));
      if (curl1RM) setCurl1RM(String(Math.round(parseFloat(curl1RM) / KG_PER_LB)));
    } else {
      const ft = parseFloat(heightFt) || 0;
      const inch = parseFloat(heightIn) || 0;
      const totalInches = ft * 12 + inch;
      if (totalInches > 0) {
        setHeightCm(String(Math.round(totalInches * CM_PER_INCH)));
      } else {
        setHeightCm('');
      }
      const wLb = parseFloat(weightLb);
      if (wLb > 0) {
        setWeightKg(String(Math.round(wLb * KG_PER_LB)));
      } else {
        setWeightKg('');
      }
      if (waist) setWaist(String((parseFloat(waist) * CM_PER_INCH).toFixed(1)));
      if (neck) setNeck(String((parseFloat(neck) * CM_PER_INCH).toFixed(1)));
      if (hip) setHip(String((parseFloat(hip) * CM_PER_INCH).toFixed(1)));
      if (bench1RM) setBench1RM(String(Math.round(parseFloat(bench1RM) * KG_PER_LB)));
      if (squat1RM) setSquat1RM(String(Math.round(parseFloat(squat1RM) * KG_PER_LB)));
      if (curl1RM) setCurl1RM(String(Math.round(parseFloat(curl1RM) * KG_PER_LB)));
    }
    setUnit(nextUnit);
  };

  // --- Dynamic Live Calculations & Smart Badge Assignment ---
  const results = useMemo(() => {
    const hasHeight = unit === 'metric' ? parseFloat(heightCm) > 0 : (parseFloat(heightFt) > 0 || parseFloat(heightIn) > 0);
    const hasWeight = unit === 'metric' ? parseFloat(weightKg) > 0 : parseFloat(weightLb) > 0;

    // Friendly initial awaiting state when user hasn't entered data yet
    if (!hasHeight && !hasWeight) {
      return {
        isAwaiting: true,
        error: 'Enter your height, weight, and biological sex to calculate your BMI and body composition.',
      };
    }

    if (!hasHeight || !hasWeight) {
      return {
        isAwaiting: false,
        error: 'Please enter both height and weight to calculate your BMI.',
      };
    }

    if (!sex) {
      return {
        isAwaiting: false,
        error: 'Please select your biological sex to determine accurate body composition and category thresholds.',
      };
    }

    let height_cm = 0;
    let weight_kg = 0;

    if (unit === 'metric') {
      height_cm = parseFloat(heightCm) || 0;
      weight_kg = parseFloat(weightKg) || 0;
    } else {
      const ft = parseFloat(heightFt) || 0;
      const inch = parseFloat(heightIn) || 0;
      height_cm = (ft * 12 + inch) * CM_PER_INCH;
      weight_kg = (parseFloat(weightLb) || 0) * KG_PER_LB;
    }

    if (height_cm <= 50 || weight_kg <= 15) {
      return {
        isAwaiting: false,
        error: 'Please enter a valid height (min 50 cm / 2 ft) and weight (min 15 kg / 35 lb).',
      };
    }

    const height_m = height_cm / 100;
    const bmi = weight_kg / (height_m * height_m);
    const bmiCategory = getBmiCategory(bmi);

    // Target healthy weight range (BMI 18.5 - 24.9)
    const minHealthyKg = 18.5 * (height_m * height_m);
    const maxHealthyKg = 24.9 * (height_m * height_m);

    // Circumferences normalized to cm
    let waist_cm = parseFloat(waist) || 0;
    let neck_cm = parseFloat(neck) || 0;
    let hip_cm = parseFloat(hip) || 0;
    if (unit === 'imperial') {
      waist_cm *= CM_PER_INCH;
      neck_cm *= CM_PER_INCH;
      hip_cm *= CM_PER_INCH;
    }

    // 1. Deurenberg Formula (BMI-derived fallback)
    const ageNum = parseFloat(age) || 30;
    const sexVal = sex === 'male' ? 1 : 0;
    let deurenbergBf = 1.2 * bmi + 0.23 * ageNum - 10.8 * sexVal - 5.4;
    deurenbergBf = Math.max(sex === 'male' ? 3 : 10, Math.min(65, deurenbergBf));

    // 2. U.S. Navy Tape Method
    let navyBf = null;
    let navyError = null;
    const hasTape = waist_cm > 0 && neck_cm > 0 && (sex === 'male' || (sex === 'female' && hip_cm > 0));

    if (hasTape) {
      try {
        if (sex === 'male') {
          if (waist_cm <= neck_cm) {
            throw new Error('Waist must be larger than neck circumference for males.');
          }
          navyBf = 86.010 * Math.log10(waist_cm - neck_cm) - 70.041 * Math.log10(height_cm) + 30.30;
        } else {
          if (waist_cm + hip_cm <= neck_cm) {
            throw new Error('Waist + Hip must exceed neck circumference for females.');
          }
          navyBf = 163.205 * Math.log10(waist_cm + hip_cm - neck_cm) - 97.684 * Math.log10(height_cm) - 104.91;
        }
        navyBf = Math.max(sex === 'male' ? 2 : 10, Math.min(65, navyBf));
      } catch (err) {
        navyError = err.message;
      }
    } else if (sex === 'female' && waist_cm > 0 && neck_cm > 0 && hip_cm <= 0) {
      navyError = 'Hip circumference is required for females to compute the U.S. Navy Body Fat %.';
    }

    // 3. Athletic Strength Modifier
    let finalBf = navyBf !== null ? navyBf : deurenbergBf;
    let methodUsed = navyBf !== null ? 'U.S. Navy Tape Method' : 'Deurenberg Formula (BMI-derived)';
    let strengthAdjustment = 0;
    let csi = null;

    if (!doesNotLift) {
      const bp = parseFloat(bench1RM) ? (unit === 'imperial' ? parseFloat(bench1RM) * KG_PER_LB : parseFloat(bench1RM)) : 0;
      const sq = parseFloat(squat1RM) ? (unit === 'imperial' ? parseFloat(squat1RM) * KG_PER_LB : parseFloat(squat1RM)) : 0;
      const crl = parseFloat(curl1RM) ? (unit === 'imperial' ? parseFloat(curl1RM) * KG_PER_LB : parseFloat(curl1RM)) : 0;

      if (bp > 0 && sq > 0 && weight_kg > 0) {
        const rs_bench = bp / weight_kg;
        const rs_squat = sq / weight_kg;
        const rs_curl = crl > 0 ? crl / weight_kg : 0;

        csi = crl > 0
          ? 0.4 * rs_bench + 0.5 * rs_squat + 0.1 * rs_curl
          : (0.4 * rs_bench + 0.5 * rs_squat) / 0.9;

        if (csi >= 2.0) strengthAdjustment = -3.0;
        else if (csi >= 1.6) strengthAdjustment = -2.0;
        else if (csi >= 1.2) strengthAdjustment = -1.0;
        else if (csi >= 0.8) strengthAdjustment = 0;
        else if (csi >= 0.5) strengthAdjustment = +1.0;
        else strengthAdjustment = +2.0;

        if (strengthAdjustment < 0 && (bmi >= 35 || finalBf >= 40)) {
          strengthAdjustment = 0;
        }

        finalBf = Math.max(sex === 'male' ? 2 : 10, finalBf + strengthAdjustment);
        if (strengthAdjustment !== 0) {
          methodUsed += ` + Strength Modifier (${strengthAdjustment > 0 ? '+' : ''}${strengthAdjustment}%)`;
        }
      }
    }

    const bfCategory = getBodyFatCategory(finalBf, sex);

    // 4. Waist-to-Height Ratio (WHtR) & Waist-to-Hip Ratio (WHR)
    const whtr = waist_cm > 0 && height_cm > 0 ? waist_cm / height_cm : null;
    let whtrRisk = 'Optimal';
    if (whtr >= 0.6) whtrRisk = 'Substantially Elevated Risk';
    else if (whtr >= 0.5) whtrRisk = 'Increased Cardiometabolic Risk';

    const whr = waist_cm > 0 && hip_cm > 0 ? waist_cm / hip_cm : null;
    let whrRisk = 'Optimal';
    if (whr) {
      if ((sex === 'male' && whr > 0.90) || (sex === 'female' && whr > 0.85)) {
        whrRisk = 'High Abdominal Adiposity';
      }
    }

    // 5. Lean Mass & Fat Mass
    const fatMassKg = weight_kg * (finalBf / 100);
    const leanMassKg = weight_kg - fatMassKg;

    // 6. Basal Metabolic Rate (BMR)
    const bmr = 370 + 21.6 * leanMassKg;

    // 7. Overall Composite Status Badge (Auto adjusts to Green / Orange / Red)
    let overallBadgeClass = 'badge-green';
    let overallLabel = 'Normal weight';

    if (bmi >= 30 || bfCategory.label === 'Obese' || (whtr !== null && whtr >= 0.60) || (whrRisk === 'High Abdominal Adiposity')) {
      overallBadgeClass = 'badge-red';
      if (bmi >= 40) overallLabel = 'Obesity Class III';
      else if (bmi >= 35) overallLabel = 'Obesity Class II';
      else if (bmi >= 30) overallLabel = 'Obesity Class I';
      else if (whrRisk === 'High Abdominal Adiposity') overallLabel = 'High Central Adiposity';
      else overallLabel = 'High Adiposity (Obese)';
    } else if (bmi >= 25 || bfCategory.label === 'Average' || (whtr !== null && whtr >= 0.50) || bmi < 18.5) {
      if (bmi >= 25 && bmi < 30 && (bfCategory.label === 'Athletes' || bfCategory.label === 'Fitness')) {
        overallBadgeClass = 'badge-green';
        overallLabel = 'Athletic · High Muscle Mass';
      } else {
        overallBadgeClass = 'badge-orange';
        if (bmi < 18.5) overallLabel = 'Underweight';
        else if (whtr !== null && whtr >= 0.50) overallLabel = 'Elevated Waist Risk';
        else overallLabel = 'Pre-obesity (Overweight)';
      }
    } else {
      overallBadgeClass = 'badge-green';
      if (bfCategory.label === 'Athletes') overallLabel = 'Athletic · Optimal';
      else if (bfCategory.label === 'Fitness') overallLabel = 'Fitness · Optimal';
      else overallLabel = 'Normal weight';
    }

    return {
      isAwaiting: false,
      bmi,
      bmiCategory,
      finalBf,
      bfCategory,
      overallBadgeClass,
      overallLabel,
      navyBf,
      deurenbergBf,
      methodUsed,
      strengthAdjustment,
      csi,
      fatMassKg,
      leanMassKg,
      weight_kg,
      height_cm,
      minHealthyKg,
      maxHealthyKg,
      whtr,
      whtrRisk,
      whr,
      whrRisk,
      bmr,
      navyError,
      error: null,
    };
  }, [
    unit,
    sex,
    age,
    heightCm,
    heightFt,
    heightIn,
    weightKg,
    weightLb,
    waist,
    neck,
    hip,
    doesNotLift,
    bench1RM,
    squat1RM,
    curl1RM,
  ]);

  return (
    <div className="bmiBodyfatCalculator">
      {/* Hero Header */}
      <div className="bmi-hero">
        <div>
          <p className="bmi-eyebrow">Pure Health Nexus / Body Composition Tools</p>
          <h1>Measure what matters.<br />Beyond the scale.</h1>
          <p className="bmi-hero-desc">
            Calculate your clinical BMI, true body fat percentage, lean tissue mass, and cardiometabolic markers.
          </p>

          {/* AI Promo Callout Banner */}
          <div className="ai-promo-inline">
            <strong>Unlock Smarter Eating:</strong> Get a complete breakdown of vitamins, minerals, and fats plus a personalized weight-loss plan or nutrition tailored to your training with a single{' '}
            <a href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals" className="ai-promo-link">
              AI scan
            </a>
          </div>
        </div>

        <div className="bmi-hero-art" aria-hidden="true">
          <div className="bmi-art-bars">
            <span className="bmi-art-bar" style={{ height: '35px' }} />
            <span className="bmi-art-bar" style={{ height: '55px' }} />
            <span className="bmi-art-bar" style={{ height: '42px' }} />
            <span className="bmi-art-bar" style={{ height: '68px' }} />
            <span className="bmi-art-bar" style={{ height: '50px' }} />
          </div>
          <span className="bmi-art-caption">CLINICAL PRECISION</span>
        </div>
      </div>

      <div className="bmi-layout">
        {/* Left Column: Form Panel */}
        <div className="bmi-panel bmi-input-panel">
          <div className="bmi-panel-heading">
            <div>
              <p className="bmi-eyebrow">01 / Profile</p>
              <h2>Enter Measurements</h2>
              <p>Type your stats below. Results and badges auto-adjust in real-time.</p>
            </div>
            <span className="bmi-step">01</span>
          </div>

          {/* Unit Toggle */}
          <div className="bmi-field">
            <span className="bmi-field-label">Measurement System</span>
            <div className="bmi-segmented" role="group" aria-label="Measurement units">
              <button
                type="button"
                aria-pressed={unit === 'metric'}
                onClick={() => handleUnitToggle('metric')}
              >
                Metric (cm, kg)
              </button>
              <button
                type="button"
                aria-pressed={unit === 'imperial'}
                onClick={() => handleUnitToggle('imperial')}
              >
                Imperial (ft/in, lb)
              </button>
            </div>
          </div>

          {/* Sex & Age */}
          <div className="bmi-field-row">
            <label className="bmi-field">
              <span className="bmi-field-label">Biological Sex</span>
              <select value={sex} onChange={(e) => setSex(e.target.value)}>
                <option value="">-- Select Sex --</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </label>
            <label className="bmi-field">
              <span className="bmi-field-label">Age</span>
              <input
                type="number"
                min="14"
                max="110"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="Years (e.g. 30)"
              />
            </label>
          </div>

          {/* Height & Weight */}
          <div className="bmi-section-label">Height &amp; Weight</div>
          {unit === 'metric' ? (
            <div className="bmi-field-row">
              <label className="bmi-field">
                <span className="bmi-field-label">Height (cm)</span>
                <input
                  type="number"
                  min="50"
                  max="260"
                  step="any"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  placeholder="e.g. 178"
                />
              </label>
              <label className="bmi-field">
                <span className="bmi-field-label">Weight (kg)</span>
                <input
                  type="number"
                  min="20"
                  max="400"
                  step="any"
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  placeholder="e.g. 78"
                />
              </label>
            </div>
          ) : (
            <div className="bmi-field-row">
              <div className="bmi-field">
                <span className="bmi-field-label">Height (ft / in)</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={heightFt}
                    onChange={(e) => setHeightFt(e.target.value)}
                    placeholder="ft"
                  />
                  <input
                    type="number"
                    min="0"
                    max="11.9"
                    step="any"
                    value={heightIn}
                    onChange={(e) => setHeightIn(e.target.value)}
                    placeholder="in"
                  />
                </div>
              </div>
              <label className="bmi-field">
                <span className="bmi-field-label">Weight (lb)</span>
                <input
                  type="number"
                  min="40"
                  max="900"
                  step="any"
                  value={weightLb}
                  onChange={(e) => setWeightLb(e.target.value)}
                  placeholder="e.g. 172"
                />
              </label>
            </div>
          )}

          {/* Body Circumferences */}
          <div className="bmi-section-label">
            <span>Body Circumferences</span>
            <small style={{ fontWeight: 400, color: 'var(--bmi-muted)', textTransform: 'none' }}>
              {sex === 'female' ? 'Navy BF% & WHR' : 'Waist/Neck (BF%) · Hips (WHR)'}
            </small>
          </div>

          <div className="bmi-field-row">
            <label className="bmi-field">
              <span className="bmi-field-label">Waist ({unit === 'metric' ? 'cm' : 'in'})</span>
              <input
                type="number"
                step="any"
                value={waist}
                onChange={(e) => setWaist(e.target.value)}
                placeholder={unit === 'metric' ? 'e.g. 84' : 'e.g. 33'}
              />
            </label>
            <label className="bmi-field">
              <span className="bmi-field-label">Neck ({unit === 'metric' ? 'cm' : 'in'})</span>
              <input
                type="number"
                step="any"
                value={neck}
                onChange={(e) => setNeck(e.target.value)}
                placeholder={unit === 'metric' ? 'e.g. 38' : 'e.g. 15'}
              />
            </label>
          </div>

          {/* Hips Input: Always available so males and females can calculate WHR */}
          <label className="bmi-field">
            <span className="bmi-field-label">
              <span>Hips ({unit === 'metric' ? 'cm' : 'in'})</span>
              <small style={{ textTransform: 'none', fontWeight: 500, color: 'var(--bmi-muted)' }}>
                {sex === 'female' ? 'Required for Navy BF% & WHR' : 'Required for Waist-to-Hip Ratio (WHR)'}
              </small>
            </span>
            <input
              type="number"
              step="any"
              value={hip}
              onChange={(e) => setHip(e.target.value)}
              placeholder={unit === 'metric' ? 'e.g. 98' : 'e.g. 38.5'}
            />
          </label>

          <p className="bmi-hint">
            Waist &amp; Neck evaluate body fat %. Waist &amp; Hips evaluate Waist-to-Hip Ratio (WHR).{' '}
            <a href="#measurement-guide" className="bmi-link-inline">
              Measurement guide ↓
            </a>
          </p>

          {/* Optional Strength Data */}
          <div className="bmi-section-label">
            <span>Strength Data</span>
            <small style={{ fontWeight: 400, color: 'var(--bmi-muted)', textTransform: 'none' }}>Optional 1RM</small>
          </div>

          <label className="bmi-checkbox-label">
            <input
              type="checkbox"
              checked={doesNotLift}
              onChange={(e) => setDoesNotLift(e.target.checked)}
            />
            I do not strength train (skip athletic calibration)
          </label>

          {!doesNotLift && (
            <>
              <div className="bmi-field-row">
                <label className="bmi-field">
                  <span className="bmi-field-label">Bench 1RM ({unit === 'metric' ? 'kg' : 'lb'})</span>
                  <input
                    type="number"
                    step="any"
                    value={bench1RM}
                    onChange={(e) => setBench1RM(e.target.value)}
                    placeholder="e.g. 100"
                  />
                </label>
                <label className="bmi-field">
                  <span className="bmi-field-label">Squat 1RM ({unit === 'metric' ? 'kg' : 'lb'})</span>
                  <input
                    type="number"
                    step="any"
                    value={squat1RM}
                    onChange={(e) => setSquat1RM(e.target.value)}
                    placeholder="e.g. 120"
                  />
                </label>
              </div>
              <label className="bmi-field">
                <span className="bmi-field-label">Arm Curl 1RM ({unit === 'metric' ? 'kg' : 'lb'})</span>
                <input
                  type="number"
                  step="any"
                  value={curl1RM}
                  onChange={(e) => setCurl1RM(e.target.value)}
                  placeholder="Optional"
                />
              </label>
            </>
          )}

          {/* Goal Selector */}
          <label className="bmi-field" style={{ marginTop: '10px' }}>
            <span className="bmi-field-label">Primary Body Goal</span>
            <select value={goal} onChange={(e) => setGoal(e.target.value)}>
              <option value="maintain">Maintain Current Composition</option>
              <option value="lose_fat">Lose Fat / Weight</option>
              <option value="gain_muscle">Gain Muscle Mass</option>
            </select>
          </label>

          {results.navyError && (
            <div className="bmi-error-notice">{results.navyError}</div>
          )}
        </div>

        {/* Right Column: Results & Analytics */}
        <div className="bmi-results">
          {results.error ? (
            <div className="bmi-panel bmi-result-hero">
              <div className="bmi-result-top">
                <div>
                  <p className="bmi-eyebrow">02 / Assessment</p>
                  <h2>Body Composition Profile</h2>
                </div>
                <span className={`bmi-badge ${results.isAwaiting ? 'badge-orange' : 'badge-red'}`}>
                  {results.isAwaiting ? 'Awaiting Input' : 'Incomplete Input'}
                </span>
              </div>
              <div style={{ padding: '16px 0 6px' }}>
                <p style={{ fontSize: '1rem', color: 'var(--bmi-text)', margin: '0 0 8px' }}>
                  {results.error}
                </p>
                <p style={{ fontSize: '0.85rem', color: 'var(--bmi-muted)', margin: 0 }}>
                  Enter your height, weight, and biological sex in the panel on the left to generate your clinical BMI, body fat percentage, lean mass breakdown, and health risk status.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Primary Assessment Hero Card with Auto-Adjusting Badge */}
              <section className="bmi-panel bmi-result-hero">
                <div className="bmi-result-top">
                  <div>
                    <p className="bmi-eyebrow">02 / Assessment</p>
                    <h2>Body Composition Profile</h2>
                  </div>

                  {/* AUTO-ADJUSTING BADGE: Changes dynamically to Green, Orange, or Red */}
                  <span className={`bmi-badge ${results.overallBadgeClass}`}>
                    <span style={{ fontSize: '0.8em' }}>●</span> {results.overallLabel}
                  </span>
                </div>

                <div className="bmi-metrics-grid">
                  <div className="bmi-metric-cell">
                    <span className="bmi-metric-label">Estimated Body Fat</span>
                    <div className="bmi-metric-val">
                      {formatNum(results.finalBf)}
                      <span>%</span>
                    </div>
                    <div className="bmi-metric-sub">
                      <span className={`bmi-badge ${results.bfCategory.badgeClass}`} style={{ fontSize: '0.7rem', padding: '3px 9px' }}>
                        {results.bfCategory.label} ({sex})
                      </span>
                    </div>
                  </div>

                  <div className="bmi-metric-cell">
                    <span className="bmi-metric-label">Body Mass Index (BMI)</span>
                    <div className="bmi-metric-val">
                      {formatNum(results.bmi)}
                      <span> kg/m²</span>
                    </div>
                    <div className="bmi-metric-sub">
                      <span className={`bmi-badge ${results.bmiCategory.badgeClass}`} style={{ fontSize: '0.7rem', padding: '3px 9px' }}>
                        {results.bmiCategory.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Visual Spectrum Bar for BMI */}
                <div className="bmi-gauge-wrap">
                  <div className="bmi-gauge-bar">
                    <div className="bmi-gauge-seg seg-under" title="Underweight (< 18.5) · Orange" />
                    <div className="bmi-gauge-seg seg-normal" title="Normal (18.5 - 24.9) · Green" />
                    <div className="bmi-gauge-seg seg-over" title="Pre-obesity (25 - 29.9) · Orange" />
                    <div className="bmi-gauge-seg seg-obese1" title="Obesity I (30 - 34.9) · Red" />
                    <div className="bmi-gauge-seg seg-obese2" title="Obesity II+ (>= 35) · Red" />
                  </div>
                  <div
                    className="bmi-gauge-pointer"
                    style={{
                      left: `${Math.min(98, Math.max(2, ((results.bmi - 14) / (40 - 14)) * 100))}%`,
                    }}
                  />
                  <div className="bmi-gauge-labels">
                    <span>14</span>
                    <span>18.5</span>
                    <span>25.0</span>
                    <span>30.0</span>
                    <span>35.0</span>
                    <span>40+</span>
                  </div>
                </div>

                {/* Lean vs Fat Mass Split */}
                <div className="bmi-composition-split">
                  <div className="bmi-split-track">
                    <div
                      className="bmi-split-lbm"
                      style={{ width: `${100 - results.finalBf}%` }}
                    >
                      {formatNum(100 - results.finalBf)}% Lean
                    </div>
                    <div
                      className="bmi-split-fm"
                      style={{ width: `${results.finalBf}%` }}
                    >
                      {formatNum(results.finalBf)}% Fat
                    </div>
                  </div>
                  <div className="bmi-composition-legend">
                    <span>
                      <span className="bmi-dot dot-lbm" /> Lean Mass:{' '}
                      <b>
                        {formatNum(unit === 'metric' ? results.leanMassKg : results.leanMassKg / KG_PER_LB)}{' '}
                        {unit === 'metric' ? 'kg' : 'lb'}
                      </b>
                    </span>
                    <span>
                      <span className="bmi-dot dot-fm" /> Fat Mass:{' '}
                      <b>
                        {formatNum(unit === 'metric' ? results.fatMassKg : results.fatMassKg / KG_PER_LB)}{' '}
                        {unit === 'metric' ? 'kg' : 'lb'}
                      </b>
                    </span>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="bmi-stats">
                  <div>
                    <small>Calculation Method</small>
                    <strong style={{ fontSize: '0.92rem' }}>{results.methodUsed}</strong>
                  </div>
                  <div>
                    <small>Normal Weight Range</small>
                    <strong>
                      {unit === 'metric'
                        ? `${formatNum(results.minHealthyKg, 0)} - ${formatNum(results.maxHealthyKg, 0)} kg`
                        : `${formatNum(results.minHealthyKg / KG_PER_LB, 0)} - ${formatNum(results.maxHealthyKg / KG_PER_LB, 0)} lb`}
                    </strong>
                  </div>
                  <div>
                    <small>Basal Metabolic Rate</small>
                    <strong>{formatNum(results.bmr, 0)} kcal/day</strong>
                  </div>
                </div>
              </section>

              {/* Cardiometabolic & Health Risk Cards */}
              <section className="bmi-panel bmi-module">
                <div className="bmi-module-heading">
                  <p className="bmi-eyebrow">03 / Health Indicators</p>
                  <h2>Cardiometabolic Risk Screening</h2>
                  <p>Central fat distribution metrics evaluated against clinical standards.</p>
                </div>

                <div className="bmi-cards-grid">
                  <div className="bmi-card">
                    <h4>Waist-to-Height Ratio (WHtR)</h4>
                    <div className="bmi-card-stat">
                      {results.whtr ? formatNum(results.whtr, 2) : '--'}
                    </div>
                    <p className="bmi-card-desc">
                      {results.whtr ? (
                        <>
                          Status:{' '}
                          <strong style={{ color: results.whtr >= 0.5 ? 'var(--bmi-orange)' : 'var(--bmi-green)' }}>
                            {results.whtrRisk}
                          </strong>
                          . Clinical standard recommends maintaining a ratio &lt; 0.50.
                        </>
                      ) : (
                        'Enter waist measurement above to evaluate central adiposity and cardiometabolic risk.'
                      )}
                    </p>
                  </div>

                  <div className="bmi-card">
                    <h4>Waist-to-Hip Ratio (WHR)</h4>
                    <div className="bmi-card-stat">
                      {results.whr ? formatNum(results.whr, 2) : '--'}
                    </div>
                    <p className="bmi-card-desc">
                      {results.whr ? (
                        <>
                          Status:{' '}
                          <strong style={{ color: results.whrRisk === 'Optimal' ? 'var(--bmi-green)' : 'var(--bmi-red)' }}>
                            {results.whrRisk}
                          </strong>
                          . (WHO cutoff: &gt; 0.90 for men, &gt; 0.85 for women indicates abdominal obesity).
                        </>
                      ) : (
                        'Enter hip & waist circumferences for abdominal fat distribution analysis.'
                      )}
                    </p>
                  </div>
                </div>
              </section>

              {/* Analytical Breakdown Table */}
              <section className="bmi-panel bmi-module">
                <div className="bmi-module-heading">
                  <p className="bmi-eyebrow">04 / Breakdown</p>
                  <h2>Analytical Breakdown & Verification</h2>
                  <p>Comparing formula models and athletic strength adjustments.</p>
                </div>

                <div className="bmi-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Metric / Calculation</th>
                        <th>Value</th>
                        <th>Standard / Status</th>
                        <th>Clinical Context</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Body Mass Index (BMI)</strong></td>
                        <td>{formatNum(results.bmi)} kg/m²</td>
                        <td><span className={`bmi-badge ${results.bmiCategory.badgeClass}`}>{results.bmiCategory.label}</span></td>
                        <td>Height-to-weight screening tool (WHO classification).</td>
                      </tr>
                      <tr>
                        <td><strong>U.S. Navy Tape Method</strong></td>
                        <td>{results.navyBf !== null ? `${formatNum(results.navyBf)}%` : 'Awaiting tape input'}</td>
                        <td>{results.navyBf !== null ? <span className="bmi-badge badge-green">Tape Standard</span> : 'Optional'}</td>
                        <td>Directly correlates with DEXA & hydrostatic weighing (±3-4%).</td>
                      </tr>
                      <tr>
                        <td><strong>Deurenberg Formula</strong></td>
                        <td>{formatNum(results.deurenbergBf)}%</td>
                        <td><span className="bmi-badge badge-orange">Population Model</span></td>
                        <td>Age- and sex-adjusted estimation derived from BMI.</td>
                      </tr>
                      {results.csi !== null && (
                        <tr>
                          <td><strong>Strength Modifier (CSI)</strong></td>
                          <td>{formatNum(results.csi, 2)} CSI</td>
                          <td>{results.strengthAdjustment >= 0 ? `+${results.strengthAdjustment}%` : `${results.strengthAdjustment}%`}</td>
                          <td>Empirical lean mass adjustment for strength-trained athletes.</td>
                        </tr>
                      )}
                      <tr>
                        <td><strong>Lean Body Mass (LBM)</strong></td>
                        <td>
                          {formatNum(unit === 'metric' ? results.leanMassKg : results.leanMassKg / KG_PER_LB)}{' '}
                          {unit === 'metric' ? 'kg' : 'lb'}
                        </td>
                        <td><span className="bmi-badge badge-green">Metabolic Driver</span></td>
                        <td>Muscle, bone, and organs; drives your basal metabolic burn.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {/* Complete Measurement Guide & Understanding Results Module */}
      <div id="measurement-guide" className="bmi-guide-section">
        <div className="bmi-module-heading">
          <p className="bmi-eyebrow">Educational Reference</p>
          <h2>Measurement Guide &amp; Understanding Results</h2>
          <p>Scientific background, measurement protocols, and clinical reference standards.</p>
        </div>

        {/* Circumference Measurement Protocol Card */}
        <div className="bmi-instruction-card">
          <h3>How to Measure Circumferences Accurately</h3>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--bmi-muted)' }}>
            Use a flexible, non-stretch tape measure. Stand relaxed, do not suck in your stomach. Ensure the tape is level (parallel to the floor).
          </p>
          <ul>
            <li>
              <strong>Neck:</strong> Measure just below the larynx (Adam's apple), looking straight ahead.
            </li>
            <li>
              <strong>Waist:</strong> Measure at the narrowest point of the torso, typically above the belly button and below the ribs. If no narrow point, measure midway between the bottom of the lowest rib and the top of the hip bone.
            </li>
            <li>
              <strong>Hips:</strong> Measure around the largest circumference of the hips and buttocks (required for females for Navy BF%, and used by both sexes for WHR).
            </li>
          </ul>
        </div>

        {/* 1. Understanding Concepts Grid */}
        <h3 style={{ color: 'var(--bmi-deep-blue)', fontSize: '1.15rem', marginBottom: '14px' }}>
          1. Understanding BMI, Body Fat %, &amp; Strength Modifier
        </h3>

        <div className="bmi-def-grid">
          <div className="bmi-def-card">
            <h4>BMI (Body Mass Index)</h4>
            <dl>
              <dt>Calculation:</dt>
              <dd>Weight (kg) / Height (m)².</dd>
              <dt>Strengths:</dt>
              <dd>Simple screening tool for weight categories. Correlated with health risks.</dd>
              <dt>Limitations:</dt>
              <dd>Doesn't measure fat directly, misclassifies muscular/lean individuals, ignores fat distribution.</dd>
            </dl>
          </div>

          <div className="bmi-def-card">
            <h4>Body Fat Percentage (BF%)</h4>
            <dl>
              <dt>Calculation (Navy Method):</dt>
              <dd>Uses height, neck, waist (and hip for females) circumferences.</dd>
              <dt>Strengths:</dt>
              <dd>Estimates body composition, better than BMI alone for some.</dd>
              <dt>Limitations:</dt>
              <dd>Circumference method error is ±3-4%. Accuracy depends on measurement technique and body shape.</dd>
            </dl>
          </div>

          <div className="bmi-def-card">
            <h4>Strength Modifier</h4>
            <dl>
              <dt>Purpose:</dt>
              <dd>To adjust the Navy BF% estimate based on relative strength (1RM lifts / body weight). Stronger individuals typically have more lean mass than the tape method alone predicts.</dd>
              <dt>Calculation:</dt>
              <dd>Creates a Composite Strength Index (CSI) from Bench, Squat, and Arm Curl relative strength. CSI maps to a body fat correction factor (± max 3%).</dd>
              <dt>Benefit:</dt>
              <dd>Provides a potentially more accurate BF% for those who strength train and offers positive feedback.</dd>
              <dt>Limitations:</dt>
              <dd>Relies on self-reported/estimated 1RMs. The correction is an approximation based on population averages.</dd>
            </dl>
          </div>

          <div className="bmi-def-card">
            <h4>Waist-to-Height Ratio (WHtR)</h4>
            <dl>
              <dt>Calculation:</dt>
              <dd>Waist Circumference / Height.</dd>
              <dt>Usefulness:</dt>
              <dd>Simple indicator of central adiposity. Ratio ≥ 0.5 linked to increased cardiometabolic risk.</dd>
            </dl>
          </div>
        </div>

        {/* 2. Reference Categories */}
        <h3 style={{ color: 'var(--bmi-deep-blue)', fontSize: '1.15rem', marginBottom: '14px' }}>
          2. Reference Categories
        </h3>

        <div className="bmi-ref-grid">
          {/* WHO BMI Categories */}
          <div className="bmi-ref-block">
            <h4>2.1 BMI Categories (WHO)</h4>
            <ul className="bmi-ref-list">
              {WHO_BMI_CATEGORIES.map((cat, idx) => (
                <li key={idx}>
                  <span>
                    <span className="bmi-ref-marker" style={{ color: cat.color }}>■</span>{' '}
                    {cat.max === Infinity ? '≥ 39.9' : `< ${cat.max}`}
                  </span>
                  <strong>{cat.label}</strong>
                </li>
              ))}
            </ul>
          </div>

          {/* ACE Body Fat Categories */}
          <div className="bmi-ref-block">
            <h4>2.2 Body Fat % Categories (ACE)</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <strong style={{ fontSize: '0.85rem', color: 'var(--bmi-deep-blue)' }}>Men</strong>
                <ul className="bmi-ref-list" style={{ marginTop: '6px' }}>
                  {ACE_BODY_FAT_CATEGORIES.male.map((item, idx) => (
                    <li key={`male-${idx}`}>
                      <span>
                        <span className="bmi-ref-marker" style={{ color: item.color }}>■</span>{' '}
                        {item.range}
                      </span>
                      <span>{item.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <strong style={{ fontSize: '0.85rem', color: 'var(--bmi-deep-blue)' }}>Women</strong>
                <ul className="bmi-ref-list" style={{ marginTop: '6px' }}>
                  {ACE_BODY_FAT_CATEGORIES.female.map((item, idx) => (
                    <li key={`female-${idx}`}>
                      <span>
                        <span className="bmi-ref-marker" style={{ color: item.color }}>■</span>{' '}
                        {item.range}
                      </span>
                      <span>{item.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Clinical Sources */}
        <div className="bmi-sources">
          <strong>Sources:</strong>
          <br />
          • World Health Organization (WHO). (n.d.). <em>Obesity and overweight</em>.
          <br />
          • American Council on Exercise (ACE). (n.d.). <em>What are the guidelines for percentage of body fat loss?</em>
          <br />
          • Hodgdon, J. A., &amp; Beckett, M. B. (1984). <em>Prediction of percent body fat for U.S. Navy men and women</em>. Naval Health Research Center Report No. 84-29.
          <br />
          • Ashwell M, Gibson S. (2016). Waist-to-height ratio as an indicator of 'early health risk'. <em>BMJ Open</em>, 6(3):e010159.
        </div>
      </div>
    </div>
  );
}

export default BMIBodyfatCalculator;